import { useRef } from 'react';
// Redux.
import { connect } from 'react-redux';

import { MoreHorizRounded } from '@mui/icons-material';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { useLocation, useNavigate } from "react-router-dom";
import { rememberRouteScroll } from '../utils/askNavigation';
import { scrollAppTo } from '../utils/appViewport';
import { useCompactBottomMenu } from '../utils/useCompactBottomMenu';
import './BottomMenu.css';

const tabIconSrc = (tab, isSelected) => (
    `/icons/tab/${tab}_${isSelected ? 'selected' : 'unselected'}.png`
);

const TabIcon = ({ tab, isSelected }) => (
    <img
        className="bottomMenuTabIcon"
        src={tabIconSrc(tab, isSelected)}
        alt=""
        aria-hidden="true"
        draggable="false"
        style={{
            display: 'block',
            objectFit: 'contain',
            userSelect: 'none',
        }}
    />
);

const MoreTabIcon = ({ isSelected }) => (
    <MoreHorizRounded
        className="bottomMenuTabIcon"
        aria-hidden="true"
        style={{
            display: 'block',
            color: 'var(--app-text)',
            opacity: isSelected ? 1 : 0.88,
        }}
    />
);

const BottomMenu = ({
    // Redux.
    authenticated,
    bottomNote,
    containerRef,
    reserveMoreTab = false,
}) => {
    const analytics = getAnalytics();
    const navigate = useNavigate();
    const location = useLocation();
    const frameRef = useRef(null);
    const { compact, expand } = useCompactBottomMenu(location.key, frameRef);

    const scrollToTop = () => {
        rememberRouteScroll(location, 0);
        scrollAppTo({ top: 0, behavior: 'smooth' });
    }

    const homeClick = () => {
        expand();
        logEvent(analytics, 'bottom_menu_home_click');
        if (!isHomePage) {
            rememberRouteScroll(location);
            navigate('/');
        } else {
            scrollToTop();
        }
    }

    const libraryClick = () => {
        expand();
        logEvent(analytics, 'bottom_menu_library_click');
        if (location.pathname !== '/library') {
            rememberRouteScroll(location);
            navigate('/library');
        } else {
            scrollToTop();
        }
    }

    const moreClick = () => {
        expand();
        logEvent(analytics, 'bottom_menu_more_click');
        if (location.pathname !== '/menu') {
            rememberRouteScroll(location);
            navigate('/menu');
        } else {
            scrollToTop();
        }
    }

    const isHomePage = location.pathname === '/' || location.pathname === '/home';
    const isLibraryPage = location.pathname.startsWith('/library') ||
        location.pathname === '/import_shows';
    const isMorePage = location.pathname.startsWith('/menu') ||
        location.pathname === '/settings/playback';

    return (
        <div
            ref={containerRef}
            className={[
                'footerNoBlur',
                bottomNote ? 'footerNoBlurWithNote' : '',
            ].filter(Boolean).join(' ')}
        >
            <div className={[
                'horizontalCenter',
                'bottomMenuFooterSurface',
                bottomNote ? 'bottomMenuFooterSurfaceWithNote' : '',
            ].filter(Boolean).join(' ')}>
                <div
                    ref={frameRef}
                    className={`bottomMenuBarFrame${compact ? ' bottomMenuBarFrameCompact' : ''}`}
                    onFocusCapture={({ target }) => {
                        // Pointer focus must not move a tab before its click completes.
                        if (target.matches(':focus-visible')) expand();
                    }}
                >
                    <div className='blurBackground bottomBar'>
                        <button className='bottomMenuButton' onClick={homeClick} aria-label="Home" aria-current={isHomePage ? 'page' : undefined}>
                            <div className='bottomMenuIcon'>
                                <TabIcon tab="home" isSelected={isHomePage} />
                            </div>
                        </button>

                        <button className='bottomMenuButton' onClick={libraryClick} aria-label="Library" aria-current={isLibraryPage ? 'page' : undefined}>
                            <div className='bottomMenuIcon'>
                                <TabIcon tab="library" isSelected={isLibraryPage} />
                            </div>
                        </button>

                        {(authenticated === true || (reserveMoreTab && authenticated !== false)) && (
                            <button className='bottomMenuButton' onClick={moreClick} aria-label="More" aria-current={isMorePage ? 'page' : undefined} disabled={authenticated !== true}>
                                <div className='bottomMenuIcon'>
                                    <MoreTabIcon isSelected={isMorePage} />
                                </div>
                            </button>
                        )}
                    </div>
                </div>
                {bottomNote ?
                <div className='bottomMenuNote'>
                    {bottomNote}
                </div>
                :
                <></>
                }
            </div>
        </div>

    );
};


const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
})

const mapActionsToProps = {
}

export default connect(mapStateToProps, mapActionsToProps)(BottomMenu);
